/*
 * Orbit Intake API client.
 *
 * Thin wrappers around the /api/intake/* endpoints. Auth is handled by
 * the global axios interceptor in services/api.ts — every request picks
 * up the auth_token cookie automatically.
 */

import axios from 'axios';

import type {
  BatchConfirmAction,
  BatchConfirmResponse,
  ConfirmResponse,
  ConverseResponse,
  NextQuestion,
  RoutePlan,
  IngestResponse,
  InquireResponse,
  SchemaResponse,
  SubmitResponse,
  CompanyContext,
  FieldStateEntry,
  FieldDef,
  PrdDocument,
} from './dossier-types';

const API_BASE = process.env.REACT_APP_API_URL ?? '';

function url(path: string): string {
  return `${API_BASE}${path}`;
}


// ── Schema ────────────────────────────────────────────────────────────

export async function fetchSchema(
  layer?: 'L1' | 'L2' | 'all',
  clientId?: string,
): Promise<SchemaResponse> {
  // `clientId` matters whenever the page is showing a company other than the
  // one on the caller's token: a consultant viewing a client, a super admin
  // impersonating, or a stale token. Without it the server falls back to the
  // token and returns the global registry, so the company's OWN added fields
  // go missing while platform-wide ones show.
  const res = await axios.get<SchemaResponse>(url('/api/intake/schema'), {
    params: { layer, client_id: clientId },
  });
  return res.data;
}


// ── State (resume) ────────────────────────────────────────────────────

export interface IntakeStatePayload {
  client_id: string;
  company_context: {
    section_a: Record<string, unknown>;
    section_b: Record<string, unknown>;
    section_c: Record<string, unknown>;
    section_d: Record<string, unknown>;
    section_e: Record<string, unknown>;
    section_f: Record<string, unknown>;
    section_g: Record<string, unknown>;
    field_states: Record<string, {
      state: 'empty' | 'ai_guessed' | 'user_confirmed' | 'user_corrected' | 'skipped';
      confidence: number;
      source: string;
      updated_at: string;
    }>;
    completion_pct: number;
  };
  latest_engagement: null | {
    id: string;
    status: string;
    routing_decision: { path: string; sub_path: string; confidence: number; reason: string };
    tier_at_decision: string;
    consultant_review_required: boolean;
    messaging_override: string | null;
    validx_project_id: string | null;
    created_at: string;
    selected_approach?: string | null;
    route_plan?: RoutePlan | null;
  };
  route_plan?: RoutePlan | null;
  /** Persisted conversation thread — used to rebuild the EXACT turns on
   *  reload (questions asked + answers given), instead of regenerating. */
  agent_thread?: Array<{ role: 'user' | 'assistant'; content: string }>;
  /** The field Orbit last asked — restores the active answerable question. */
  last_inquired_field?: string | null;
}

export async function fetchState(
  clientId: string,
  problemId?: string | null,
): Promise<IntakeStatePayload> {
  const params = problemId ? { problem_id: problemId } : undefined;
  const res = await axios.get<IntakeStatePayload>(url(`/api/intake/state/${clientId}`), { params });
  return res.data;
}


// ── Home (unified MyBusiness payload) ─────────────────────────────────

export interface HomeLayerProgress {
  captured: number;
  total: number;
  blocking_remaining: number;
}

export interface HomeBuild {
  project_id: string;
  engagement_id: string;
  problem_id: string | null;
  created_at: string | null;
  workspace_url: string | null;
  status: string;
  label: string | null;
  // The problem's OWN drafted PRD (per-problem). Present once PRD_DRAFT_READY
  // has stored it on the problem card; used to render the inline review card
  // scoped to the active problem.
  prd_document?: (PrdDocument & { title?: string }) | null;
}

export interface HomeConsultant {
  id: string;
  name: string | null;
  email: string | null;
  role: string;
  assigned_at: string | null;
  meeting_url: string | null;
}

export interface HomeActivityEntry {
  ts: string | null;
  intent: string | null;
  user_input: string;
  reply_excerpt: string;
  fields_written_count: number;
}

export interface HomePayload {
  client: {
    id: string;
    name: string | null;
    email: string | null;
    website_url: string | null;
    tier: string;
    /** Extracted from the website read (Firecrawl branding format) — logo +
     *  color palette, shown in the My Business header. Shape mirrors the
     *  stored client.branding: { logo, colors:{...}, images:{...}, fonts }. */
    branding?: {
      logo?: string | null;
      colors?: {
        primary?: string | null;
        secondary?: string | null;
        accent?: string | null;
        background?: string | null;
      } | null;
      images?: { logo?: string | null; favicon?: string | null } | null;
    } | null;
  };
  layers: {
    l1: HomeLayerProgress;
    l2: HomeLayerProgress;
  };
  engagement: null | {
    id: string;
    status: string;
    routing_decision: Record<string, unknown>;
    tier_at_decision: string;
    consultant_review_required: boolean;
    consultant_user_id: string | null;
    messaging_override: string | null;
    validx_project_id: string | null;
    created_at: string | null;
    selected_approach: string | null;
    route_plan: RoutePlan | null;
    approach_picked_at: string | null;
  };
  suggestion: null | {
    approach: string;
    rule: string;
    suggestion_line: string;
    messaging_override: string | null;
  };
  route_plan: RoutePlan | null;
  builds: HomeBuild[];
  consultant: HomeConsultant | null;
  activity: HomeActivityEntry[];
  /** Per-engagement problem-card counts. Drives the "Experience report
   *  ready" card on /my-business AutomatedLane — surfaces when at least
   *  one problem is SCOPED but no validx project has spawned yet. */
  problems_summary: {
    total: number;
    scoped: number;
  };
  // Full dossier — L1 sections (a–g) + optional L2 sections (h, i, m,
  // n, o, k, p). Matches CompanyContext so the Dossier grid can index
  // dynamically by `section_${letter}` across both layers.
  company_context: CompanyContext & {
    field_states: Record<string, FieldStateEntry & {
      pending_draft?: { value: string; proposed_at: string; source: string };
    }>;
    last_inquired_field: string | null;
  };
}

export async function fetchHome(clientId: string): Promise<HomePayload> {
  const res = await axios.get<HomePayload>(url(`/api/intake/home/${clientId}`));
  return res.data;
}


// ── Ingest ────────────────────────────────────────────────────────────

export type IngestSource = 'url' | 'paste' | 'voice' | 'doc' | 'composio';

export async function ingest(
  source: IngestSource,
  content: string,
  clientId?: string,
): Promise<IngestResponse> {
  const res = await axios.post<IngestResponse>(url('/api/intake/ingest'), {
    source,
    content,
    client_id: clientId,
  });
  return res.data;
}


// ── Inquire ───────────────────────────────────────────────────────────

export async function inquire(
  clientId?: string,
  layer?: 'L1' | 'L2',
): Promise<InquireResponse> {
  const res = await axios.post<InquireResponse>(url('/api/intake/inquire'), {
    client_id: clientId,
    layer: layer ?? null,
  });
  return res.data;
}


// ── Goals (A5) ────────────────────────────────────────────────────────
// Section D's five fields stay. These are the repeating records beside them, so
// a company can have several goals and some of them need not be about AI.

export type GoalKind = 'ai' | 'business';
export type GoalHorizon = 'this_quarter' | 'this_year' | 'next_year' | 'ongoing';
/** Mirrors the dossier's per-field vocabulary, so an inferred goal renders like
 *  a guessed field does. */
export type GoalState = 'ai_guessed' | 'user_confirmed' | 'user_corrected';

export interface GoalCard {
  id: string;
  statement: string;
  horizon: GoalHorizon | null;
  kind: GoalKind;
  state: GoalState;
  seed_source: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/** `seed` false when a surface only wants to display goals, not create them. */
export async function fetchGoals(clientId?: string, seed = true): Promise<GoalCard[]> {
  const res = await axios.get<{ goals: GoalCard[] }>(url('/api/intake/goals'), {
    params: { client_id: clientId, seed },
  });
  return res.data.goals ?? [];
}

export async function createGoal(
  body: { statement: string; horizon?: GoalHorizon | null; kind?: GoalKind; clientId?: string },
): Promise<GoalCard> {
  const res = await axios.post<{ goal: GoalCard }>(url('/api/intake/goals'), {
    statement: body.statement,
    horizon: body.horizon ?? null,
    kind: body.kind ?? 'business',
    client_id: body.clientId,
  });
  return res.data.goal;
}

export async function updateGoal(
  goalId: string,
  body: { statement?: string; horizon?: GoalHorizon | null; kind?: GoalKind; clientId?: string },
): Promise<GoalCard> {
  const res = await axios.patch<{ goal: GoalCard }>(
    url(`/api/intake/goals/${encodeURIComponent(goalId)}`),
    {
      statement: body.statement,
      horizon: body.horizon,
      kind: body.kind,
      client_id: body.clientId,
    },
  );
  return res.data.goal;
}

export async function confirmGoal(goalId: string, clientId?: string): Promise<GoalCard> {
  const res = await axios.post<{ goal: GoalCard }>(
    url(`/api/intake/goals/${encodeURIComponent(goalId)}/confirm`),
    null,
    { params: { client_id: clientId } },
  );
  return res.data.goal;
}

export async function deleteGoal(goalId: string, clientId?: string): Promise<void> {
  await axios.delete(url(`/api/intake/goals/${encodeURIComponent(goalId)}`), {
    params: { client_id: clientId },
  });
}


// ── Added fields (A6) ─────────────────────────────────────────────────
// The company adds a field we never thought to ask for. They type a LABEL;
// the backend derives the key, so nothing here builds one.

export type AddableFieldKind = 'text' | 'longtext' | 'url' | 'number' | 'date' | 'select' | 'multiselect';

export interface AddFieldBody {
  section: string;
  label: string;
  kind?: AddableFieldKind;
  placeholder?: string;
  options?: { value: string; label: string }[];
  clientId?: string;
}

export async function addDossierField(
  body: AddFieldBody,
): Promise<{ field: FieldDef; etag: string }> {
  const res = await axios.post<{ field: FieldDef; etag: string }>(
    url('/api/intake/fields'),
    {
      section: body.section,
      label: body.label,
      kind: body.kind ?? 'text',
      placeholder: body.placeholder,
      options: body.options,
      client_id: body.clientId,
    },
  );
  return res.data;
}

export async function removeDossierField(
  key: string,
  clientId?: string,
): Promise<{ removed: boolean; etag: string }> {
  const res = await axios.delete<{ removed: boolean; etag: string }>(
    url(`/api/intake/fields/${encodeURIComponent(key)}`),
    { params: clientId ? { client_id: clientId } : undefined },
  );
  return res.data;
}


// ── Confirm / correct / skip ──────────────────────────────────────────

/**
 * Write one field.
 *
 * `askNext` is the difference between a chat turn and a card row. A chat turn
 * needs the conversation advanced, which costs two model calls on the server. A
 * card row does not, and paying for them made a single card edit take nine to
 * twenty-one seconds, which reads as a row that did not save. Pass false from
 * any surface that is not a conversation; the response then carries the saved
 * sections so the row can draw itself without a second round trip.
 */
export async function confirm(
  field: string,
  value: unknown,
  action: 'confirm' | 'correct' | 'skip' = 'confirm',
  clientId?: string,
  layer?: 'L1' | 'L2',
  askNext = true,
): Promise<ConfirmResponse> {
  const res = await axios.post<ConfirmResponse>(url('/api/intake/confirm'), {
    field,
    value,
    action,
    client_id: clientId,
    layer: layer ?? null,
    ask_next: askNext,
  });
  return res.data;
}


// Slice 4 — Sharp-fill batch confirm. One round-trip for N rows so the
// BatchConfirmCard can ship 8 fields in one tap instead of 8 turns.
export async function confirmBatch(
  confirmations: BatchConfirmAction[],
  clientId?: string,
  layer?: 'L1' | 'L2',
): Promise<BatchConfirmResponse> {
  const res = await axios.post<BatchConfirmResponse>(
    url('/api/intake/confirm-batch'),
    {
      confirmations,
      client_id: clientId,
      layer: layer ?? null,
    },
  );
  return res.data;
}


// Slice 4 — email-domain pre-fill. At cold-start, infer 2-4
// high-confidence fields from the signup email and surface them in a
// BatchConfirmCard before the user types anything.
export async function fetchEmailHints(
  email: string,
  clientId?: string,
): Promise<IngestResponse> {
  const res = await axios.post<IngestResponse>(url('/api/intake/email-hints'), {
    email,
    client_id: clientId,
  });
  return res.data;
}


// Slice 12 v2 — approve the drafted PRD and route to workspace.
export interface ApprovePrdResponse {
  ok: boolean;
  engagement_id: string;
  validx_project_id: string | null;
  status: string;
}

export async function approvePrd(clientId?: string): Promise<ApprovePrdResponse> {
  const res = await axios.post<ApprovePrdResponse>(
    url('/api/intake/approve-prd'),
    { client_id: clientId },
  );
  return res.data;
}


// Slice 7 — Phase 2 entry. Lazy-creates engagement_request +
// problem_card seeded from L1.E. Idempotent: re-calling returns the
// existing problem if one exists.
export interface EnterPhase2Workflow {
  id: string;
  data: Record<string, unknown>;
  sort_order: number;
}

export interface EnterPhase2Problem {
  id: string;
  status: string;
  data: Record<string, unknown>;
  seed_source: string | null;
}

export interface EnterPhase2Response {
  ok: boolean;
  engagement_id: string;
  problem: EnterPhase2Problem | null;
  workflows: EnterPhase2Workflow[];
}

export async function enterPhase2(
  clientId?: string,
): Promise<EnterPhase2Response> {
  const res = await axios.post<EnterPhase2Response>(
    url('/api/intake/enter-phase2'),
    { client_id: clientId },
  );
  return res.data;
}


// L2 phase walk — the custom-build entry. Starts the user at Phase 1
// (Company Shell) in the intake chat. Returns the first Phase-1 question;
// the phase machine in /converse + /confirm then walks Phase 1 → 2 → 3 and
// offers the PRD only after Phase 3.
export interface EnterCustomBuildResponse {
  ok: boolean;
  // Deep scoping is a paid one-time unlock. When unpaid, the backend returns
  // ok=false + requires_payment + a Stripe checkout_url instead of Phase 1.
  requires_payment?: boolean;
  amount_cents?: number;
  checkout_url?: string | null;
  engagement_id?: string;
  phase?: string;
  next_question?: NextQuestion | null;
  route_plan?: RoutePlan | null;
}

export async function enterCustomBuild(
  clientId?: string,
  returnPath?: string,
): Promise<EnterCustomBuildResponse> {
  const res = await axios.post<EnterCustomBuildResponse>(
    url('/api/intake/enter-custom-build'),
    { client_id: clientId, return_path: returnPath },
  );
  return res.data;
}


// Verify a deep-scoping payment DIRECTLY with Stripe on return (the webhook
// never reaches a local backend and can lag in prod). Flips the server-side
// paid flag so the very next enter-custom-build passes the gate into Phase 1
// instead of re-showing the $999 card. Idempotent.
export async function confirmDeepScoping(
  sessionId?: string,
  clientId?: string,
): Promise<{ ok: boolean; paid: boolean; verified?: boolean }> {
  // session_id is optional — when omitted (URL param lost), the backend falls
  // back to the session id it stashed at checkout creation.
  const res = await axios.post<{ ok: boolean; paid: boolean; verified?: boolean }>(
    url('/api/intake/deep-scoping/confirm'),
    { session_id: sessionId, client_id: clientId },
  );
  return res.data;
}


// Transcribe a recorded audio clip to RAW text (OpenAI Whisper) — no field
// extraction. The composer records → this returns the transcript → the user
// reviews it in the input and sends it as a normal turn.
export async function transcribeAudio(blob: Blob, clientId?: string | null): Promise<string> {
  const ext = blob.type.includes('mp4') ? 'm4a'
    : blob.type.includes('ogg') ? 'ogg'
    : blob.type.includes('wav') ? 'wav'
    : 'webm';
  const form = new FormData();
  form.append('file', blob, `recording.${ext}`);
  if (clientId) form.append('client_id', clientId);
  const res = await axios.post<{ text: string }>(
    url('/api/intake/transcribe'),
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return res.data?.text || '';
}


// ── Live in-browser voice (ElevenLabs Conversational AI) ────────────────
// The browser opens a real-time spoken conversation with the SAME Orbit agent
// the phone path dials. The call_token inside dynamic_variables authorizes the
// agent's server tools, so a spoken session fills the same dossier the text
// chat does — one engine, two ways in.

export interface WebVoiceSession {
  call_id: string;
  /** WebRTC token — the preferred transport (echo-cancelled, jitter-buffered). */
  conversation_token: string | null;
  /** Signed WebSocket URL — the fallback transport. */
  signed_url: string | null;
  call_token: string;
  /** Orbit's opener: it greets and asks the first question, unprompted. */
  first_message: string;
  dynamic_variables: Record<string, string>;
}

export async function startWebVoiceSession(
  opts?: { problemId?: string | null; clientId?: string | null },
): Promise<WebVoiceSession> {
  const params = new URLSearchParams();
  if (opts?.problemId) params.set('problem_id', opts.problemId);
  if (opts?.clientId) params.set('client_id', opts.clientId);
  const qs = params.toString();
  const res = await axios.get<WebVoiceSession>(
    url(`/api/intake/voice/web/session${qs ? `?${qs}` : ''}`),
  );
  return res.data;
}

export interface WebVoiceOutcome {
  ok: boolean;
  /** The problem the session was promoted into, if it captured one. */
  problem_id: string | null;
  /** The gate's read: 'draft_prd' | 'need_*' | 'solution_partner' | 'tell_me_more'. */
  verdict: string | null;
  /** True when the PRD planner was triggered off the back of this session. */
  prd_drafting: boolean;
  /** The route the session was on, so the caller knows which home to land on. */
  route: string | null;
  transcript_chars?: number;
}

/** Called when a live session ends: hands the transcript to the backend, which
 *  extracts it into the dossier and promotes it into a problem + report — the
 *  browser-side analog of the phone post-call webhook. The outcome it returns is
 *  what the caller uses to send the user on. */
export async function finalizeWebVoiceSession(
  callToken: string,
  transcript?: string,
): Promise<WebVoiceOutcome> {
  const res = await axios.post<WebVoiceOutcome>(
    url('/api/intake/voice/web/finalize'),
    { call_token: callToken, transcript: transcript || null },
  );
  return res.data;
}


// ── Map the Business, as a WALK ─────────────────────────────────────────
// One question at a time over the company's own fields, phased and resumable —
// the business sibling of the participant onboarding walk. Same dossier as the
// free-form chat, so the two doors are interchangeable at any point.

export interface WalkOption { value: string; label: string; description?: string }

export interface WalkQuestion {
  field: string;
  label: string;
  /** How to answer: a confirmation of a guess, or the field's own shape. */
  type: 'confirm' | 'single' | 'multi' | 'number' | 'open';
  /** The shape underneath a confirmation, so "no — it's X" lands in one turn. */
  answer_type: 'single' | 'multi' | 'number' | 'open';
  say: string;
  why?: string | null;
  placeholder?: string | null;
  required?: boolean;
  /** Blank is a complete answer. The walk never waits for one of these and the
   *  form lets it through without making the person hunt for "skip this one". */
  optional?: boolean;
  critical?: boolean;
  options?: WalkOption[];
  /** Present when the walk already inferred an answer and is confirming it.
   *  `source` names where it came from ("read from acme.com", "guessed from
   *  your email domain") so the person can weigh it before accepting. */
  guess?: { value: unknown; display: string; source?: string | null };
  phase: { id: string; title: string; index: number; total: number };
  progress: { answered: number; total: number };
}

export interface WalkState {
  done: boolean;
  question?: WalkQuestion;
  say?: string;
  progress: { answered: number; total: number };
  /** buttons[0] = speak the walk, buttons[1] = type it. There is no third. */
  welcome?: { title: string; voice: string; buttons: string[] };
  /** Present on the turn that answered the website: what reading it filled. */
  read?: { url: string; fields: number };
  /** The answer wasn't usable — a private repo, say. The question stands and
   *  this says why, so the person can give something we can actually read. */
  notice?: { kind: string; reason?: string; repo?: string; message: string };
  buttons?: string[];
}

export async function getBusinessWalk(clientId?: string | null): Promise<WalkState> {
  const qs = clientId ? `?client_id=${encodeURIComponent(clientId)}` : '';
  const res = await axios.get<WalkState>(url(`/api/intake/business/walk/state${qs}`));
  return res.data;
}

export async function submitBusinessWalk(
  body: { field?: string | null; value?: unknown; skip?: boolean; clientId?: string | null },
): Promise<WalkState> {
  const res = await axios.post<WalkState>(url('/api/intake/business/walk/turn'), {
    field: body.field ?? null,
    value: body.value ?? null,
    skip: !!body.skip,
    client_id: body.clientId ?? null,
  });
  return res.data;
}

export interface WalkVoiceConnection {
  signed_url: string;
  dynamic_variables: Record<string, string>;
  first_message: string;
}

/** Signed URL + variables for speaking the walk. 503 = not configured, and the
 *  walk simply stays tap-or-type. */
export async function getBusinessWalkVoice(clientId?: string | null): Promise<WalkVoiceConnection> {
  const qs = clientId ? `?client_id=${encodeURIComponent(clientId)}` : '';
  const res = await axios.get<WalkVoiceConnection>(
    url(`/api/intake/business/walk/voice/connection${qs}`),
  );
  return res.data;
}


// Slice 7 — patch problem_card.data fields inline from ProblemCanvas.
export async function patchProblem(
  problemId: string,
  fieldUpdates: Record<string, unknown>,
): Promise<{ ok: boolean }> {
  const res = await axios.patch<{ ok: boolean }>(
    url(`/api/scope/problems/${problemId}`),
    { field_updates: fieldUpdates },
  );
  return res.data;
}


// Slice 7 — append a workflow sub-card to a problem.
export async function addWorkflow(
  problemId: string,
  data: Record<string, unknown> = {},
): Promise<{ id: string; data: Record<string, unknown>; sort_order: number }> {
  const res = await axios.post<{
    id: string;
    data: Record<string, unknown>;
    sort_order: number;
  }>(url(`/api/scope/problems/${problemId}/workflows`), { data });
  return res.data;
}


// Update a workflow sub-card's data (name / trigger / steps / tools) from
// the ProblemCanvas inline editor. Bulk-merges into workflow_subcard.data.
export async function patchWorkflow(
  workflowId: string,
  data: Record<string, unknown>,
): Promise<{ ok: boolean }> {
  const res = await axios.patch<{ ok: boolean }>(
    url(`/api/scope/workflows/${workflowId}`),
    { data },
  );
  return res.data;
}


// Flip a problem_card's lifecycle status (e.g. → 'SCOPED' once it has a
// fully-scoped workflow, which is what advances Phase 2 → Phase 3).
export async function setProblemStatus(
  problemId: string,
  status: string,
): Promise<{ ok: boolean }> {
  const res = await axios.patch<{ ok: boolean }>(
    url(`/api/scope/problems/${problemId}`),
    { status },
  );
  return res.data;
}


// Slice 6 — Send-to-role magic-link delegation.
export interface ShareFieldRequest {
  field?: string;             // dotted key when sharing one question
  section?: string;           // section letter when sharing the whole section
  recipient_email: string;
  recipient_role?: string;    // 'cto' / 'cfo' / etc — defaults to field.role
  sender_name?: string;
  expires_days?: number;
  client_id?: string;
  problem_id?: string;        // bind share to active problem card
}

export interface ShareFieldResponse {
  ok: boolean;
  token_id: string;
  share_url: string;
  section: string;
  section_label: string;
  field: string | null;
  role: string | null;
  role_label: string | null;
  expires_at: string | null;
}

export async function shareField(
  body: ShareFieldRequest,
): Promise<ShareFieldResponse> {
  const res = await axios.post<ShareFieldResponse>(
    url('/api/intake/share-field'),
    body,
  );
  return res.data;
}


// ── Converse (chat turn) ──────────────────────────────────────────────

export interface ShownQuestionPayload {
  field: string | null;
  prompt: string | null;
  chip_values: string[];
}

// Persist the landing-chooser path so the backend Inquirer scopes the
// next question to it (problem → Section E; company → company sections).
// Called from RouteChooser.onPick before the first converse so the very
// first question respects the picked path — survives reload.
export async function selectRoute(
  route: 'problem' | 'company' | 'explore',
  clientId?: string,
): Promise<{ ok: boolean; route: string }> {
  const res = await axios.post<{ ok: boolean; route: string }>(
    url('/api/intake/select-route'),
    { route, client_id: clientId },
  );
  return res.data;
}


// Start a fresh problem — clears the shared working slot (section_e + e.*
// states + L2) and asserts the problem route. Used by "Scope a problem" so
// the new problem doesn't resume the previously-scoped one.
export async function newProblemReset(
  clientId?: string,
): Promise<{ ok: boolean }> {
  const res = await axios.post<{ ok: boolean }>(
    url('/api/intake/new-problem'),
    { client_id: clientId },
  );
  return res.data;
}


// Check a specific problem out into the working slot so /state + /converse
// resume THAT problem (its captured fields + next question). The per-problem
// Orbit panel calls this on open.
export async function resumeProblem(
  problemId: string,
  clientId?: string,
): Promise<{ ok: boolean }> {
  const res = await axios.post<{ ok: boolean }>(
    url('/api/intake/resume-problem'),
    { problem_id: problemId, client_id: clientId },
  );
  return res.data;
}


// Proactive coaching — fetch generated grid-block candidates for a free-type
// problem field (success_criteria, desired_workflow, …) so the UI can offer
// "pick one or write your own" alongside the question. Empty list = not a
// coachable field (just show the input).
export async function fetchFieldCandidates(
  field: string,
  clientId?: string,
): Promise<{ field: string; label?: string; intro?: string; candidates: Array<{ value: string; title?: string; rationale?: string }> }> {
  const res = await axios.post(url('/api/intake/field-candidates'), { field, client_id: clientId });
  return res.data;
}


export async function converse(
  input: string,
  currentField: string | null,
  clientId?: string,
  shownQuestion?: ShownQuestionPayload | null,
  layer?: 'L1' | 'L2',
  opts?: ConverseOptions,
): Promise<ConverseResponse> {
  // Stream under the hood so EVERY caller (scope, resume, ingest, answer)
  // gets the SSE keepalive — a slow turn never sits silent past the
  // browser/ELB idle window, which is what produced the prod 499 ("I hit a
  // snag"). We resolve with the final `done` payload, so the return shape is
  // identical to the old blocking call and no call site changes.
  //
  // Fallback to the blocking endpoint ONLY when the stream never established
  // (so a turn that already ran server-side is never double-processed).
  let finalResult: ConverseResponse | null = null;
  let firstEvent = false;
  try {
    await converseStream(input, currentField, clientId, shownQuestion, layer, opts, {
      onThinking: () => { firstEvent = true; },
      onUnderstood: () => { firstEvent = true; },
      onReply: () => { firstEvent = true; },
      onDone: (r) => { finalResult = r; },
    });
  } catch (e) {
    if (firstEvent) throw e; // ran server-side; don't double-run via blocking
    // never established → safe to fall through to the blocking call below
  }
  if (finalResult) return finalResult;
  if (firstEvent) throw new Error('converse stream ended without a result');

  const res = await axios.post<ConverseResponse>(url('/api/orbit/turn'), {
    input,
    current_field: currentField,
    client_id: clientId,
    shown_question: shownQuestion ?? null,
    layer: layer ?? null,
    // Multi-problem (P2b): bind this turn to a problem record. new_problem
    // starts a fresh problem (clears the slot); problem_id resumes one.
    problem_id: opts?.problemId ?? null,
    new_problem: opts?.newProblem ?? false,
    work_route: opts?.workRoute ?? null,
    source: opts?.source ?? null,
    provider: opts?.provider ?? null,
    route: opts?.route ?? null,
  });
  return res.data;
}

export interface ConverseOptions {
  problemId?: string | null;
  newProblem?: boolean;
  workRoute?: string | null;
  source?: string | null;
  provider?: string | null;
  // The landing route this turn is on ('problem' | 'company' | 'explore'), sent
  // so the backend re-asserts it: a stale per-client 'problem' route would
  // otherwise hijack the company (map-the-business) walk. null leaves it alone.
  route?: string | null;
}


// ── Streaming converse (SSE) ──────────────────────────────────────────
// Same turn as converse() but consumed as Server-Sent Events so the UI
// renders progressively (thinking → ack → reply chunks → next question)
// and the connection never idles out — which is what kills the prod 499
// ("I hit a snag"). Handlers fire per event; onDone carries the full
// ConverseResponse, applied exactly like the blocking path. Throws on a
// transport failure so the caller can fall back to blocking converse().
export interface ConverseStreamHandlers {
  onThinking?: () => void;
  onUnderstood?: (ack: string) => void;
  onReply?: (chunk: string) => void;
  onNextQuestion?: (nq: NextQuestion) => void;
  onDirective?: (directiveType: string, payload: unknown, nq: NextQuestion) => void;
  onDone?: (result: ConverseResponse) => void;
  onError?: (message: string) => void;
}

export async function converseStream(
  input: string,
  currentField: string | null,
  clientId: string | undefined,
  shownQuestion: ShownQuestionPayload | null | undefined,
  layer: 'L1' | 'L2' | undefined,
  opts: ConverseOptions | undefined,
  handlers: ConverseStreamHandlers,
): Promise<void> {
  let token: string | null = null;
  try { token = localStorage.getItem('auth_token'); } catch { /* ignore */ }
  const res = await fetch(url('/api/orbit/turn/stream'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      input,
      current_field: currentField,
      client_id: clientId,
      shown_question: shownQuestion ?? null,
      layer: layer ?? null,
      problem_id: opts?.problemId ?? null,
      new_problem: opts?.newProblem ?? false,
      work_route: opts?.workRoute ?? null,
      source: opts?.source ?? null,
      provider: opts?.provider ?? null,
      route: opts?.route ?? null,
    }),
  });
  if (!res.ok || !res.body) {
    throw new Error(`converse/stream HTTP ${res.status}`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    // SSE frames are separated by a blank line.
    let sep: number;
    while ((sep = buf.indexOf('\n\n')) !== -1) {
      const frame = buf.slice(0, sep);
      buf = buf.slice(sep + 2);
      const dataLine = frame.split('\n').find(l => l.startsWith('data:'));
      if (!dataLine) continue; // comment/keepalive (": ping") — skip
      const rawJson = dataLine.slice(5).trim();
      if (!rawJson) continue;
      let ev: { type?: string; [k: string]: unknown };
      try { ev = JSON.parse(rawJson); } catch { continue; }
      switch (ev.type) {
        case 'thinking': handlers.onThinking?.(); break;
        case 'understood': handlers.onUnderstood?.((ev.ack as string) || ''); break;
        case 'reply': handlers.onReply?.((ev.chunk as string) || ''); break;
        case 'next_question': handlers.onNextQuestion?.(ev.next_question as NextQuestion); break;
        case 'directive':
          handlers.onDirective?.(
            ev.directive_type as string,
            ev.directive_payload,
            ev.next_question as NextQuestion,
          );
          break;
        case 'done': handlers.onDone?.(ev.result as ConverseResponse); break;
        case 'error': handlers.onError?.((ev.message as string) || 'stream error'); break;
      }
    }
  }
}


// ── Direct PRD kickoff (bypasses the agent) ───────────────────────────
//
// When the user clicks Draft on the propose_prd_draft directive card,
// we hit this endpoint directly instead of round-tripping through
// /converse + Sonnet. The agent was conversationally acknowledging the
// click ("OK drafting!") without actually calling the draft_prd tool —
// no engagement row, no validx project. This endpoint guarantees the
// commit step fires.

export interface DraftPrdNowResponse {
  engagement_id: string;
  prd_drafting: boolean;
  prd_draft_title: string;
}

// ── Paid Human (Solution Partner) slot booking ─────────────────────
// Mock today — picks the slot, persists the choice on
// engagement_request.routing_decision. When the cal.com integration
// lands, this endpoint will also POST to /bookings on cal.com and
// store the real booking_id alongside.

export interface PaidHumanBookResponse {
  engagement_id: string;
  booked_at: string;
  date_label: string;
  time_label: string;
}

export async function bookPaidHumanSlot(args: {
  clientId?: string;
  slotId: string;
  dateLabel: string;
  timeLabel: string;
  consultantName?: string;
  consultantRole?: string;
}): Promise<PaidHumanBookResponse> {
  const res = await axios.post<PaidHumanBookResponse>(url('/api/intake/paid-human/book'), {
    client_id: args.clientId,
    slot_id: args.slotId,
    date_label: args.dateLabel,
    time_label: args.timeLabel,
    consultant_name: args.consultantName,
    consultant_role: args.consultantRole,
  });
  return res.data;
}


export async function draftPrdNow(args: {
  clientId?: string;
  title: string;
  summary?: string;
  integrations?: string[];
  successMetric?: string;
  extraContext?: string;
}): Promise<DraftPrdNowResponse> {
  const res = await axios.post<DraftPrdNowResponse>(url('/api/intake/draft-prd-now'), {
    client_id: args.clientId,
    title: args.title,
    summary: args.summary,
    integrations: args.integrations,
    success_metric: args.successMetric,
    extra_context: args.extraContext,
  });
  return res.data;
}


// ── Ingest binary (voice + doc) ───────────────────────────────────────

export async function ingestFile(
  source: 'voice' | 'doc' | 'csv' | 'image',
  file: Blob,
  filename: string,
  /** A consultant ingesting for a client they are assigned to. The server
   *  refuses the override for every other role. */
  clientId?: string | null,
): Promise<IngestResponse> {
  const form = new FormData();
  form.append('source', source);
  form.append('file', file, filename);
  if (clientId) form.append('client_id', clientId);
  const res = await axios.post<IngestResponse>(url('/api/intake/ingest-file'), form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}


// ── Submit (lock routing) ─────────────────────────────────────────────

export async function submit(
  selectedApproach?: 'automated' | 'paid_human' | 'explore',
): Promise<SubmitResponse> {
  const res = await axios.post<SubmitResponse>(url('/api/intake/submit'), {
    selected_approach: selectedApproach ?? null,
  });
  return res.data;
}

export async function updateApproach(
  selectedApproach: 'automated' | 'paid_human' | 'explore',
): Promise<SubmitResponse> {
  const res = await axios.post<SubmitResponse>(url('/api/intake/approach'), {
    selected_approach: selectedApproach,
  });
  return res.data;
}


// ── Delegated share (PUBLIC — no auth) ────────────────────────────────
// A recipient opens /orbit/delegated/<token>. These hit the public
// token endpoints. We use a BARE axios instance (axios.create) so the
// global auth interceptor in services/api.ts never attaches a stale
// token or trips its 401 handler — the recipient is unauthenticated by
// design; the token in the URL is the auth boundary.

const _publicAxios = axios.create();

export interface DelegatedField extends FieldDef {
  /** Existing value already in the dossier (prefill). */
  value?: unknown;
}

export interface DelegatedPayload {
  ok: boolean;
  token_id: string;
  company_name?: string | null;
  // The person who delegated this (the user who sent the link). Shown to the
  // recipient ("Sarah asked me to…") and thanked on their behalf.
  delegated_by?: string | null;
  section: string;
  section_label: string;
  role?: string | null;
  role_label?: string | null;
  expires_at?: string | null;
  completed_at?: string | null;
  fields: DelegatedField[];
}

export interface DelegatedSubmitResponse {
  ok: boolean;
  written: string[];
  errors: Array<{ field: string; error: string }>;
  count: number;
}

/** Fetch the token's scoped questions. Throws on 404 (invalid/expired). */
export async function getDelegated(tokenId: string): Promise<DelegatedPayload> {
  const res = await _publicAxios.get<DelegatedPayload>(
    url(`/api/intake/delegated/${tokenId}`),
  );
  return res.data;
}

/** Submit the recipient's answers. `answers` is a { fieldKey: value } map. */
export async function submitDelegated(
  tokenId: string,
  answers: Record<string, unknown>,
  respondentName?: string,
): Promise<DelegatedSubmitResponse> {
  const res = await _publicAxios.post<DelegatedSubmitResponse>(
    url(`/api/intake/delegated/${tokenId}`),
    { answers, respondent_name: respondentName ?? null },
  );
  return res.data;
}
